import React, { createContext, useState, useEffect } from "react";
import { getUser, getUserFollowingList, getUserProposalVotes } from "services/user-service";
import { get_site_settings, backend_login, backend_refresh_token, backend_get_challenge } from 'services/db-service';
import { extractSessionData } from 'helpers/localStorage';
import * as dsteem from 'dsteem';

const storage = {
  USER: "user",
  FOLLOWING:"following",
  METADATA:"metadata",
  RECENTLY_VIEWED:"recently_viewed",
  NOTIFICATIONS_VIEWED:"notifications_viewed",
  RPC_NODE:"rpc_node",
  APP_SETTINGS:"app_settings",
  POST_DRAFT:"post_draft",
  PROPOSALS: "proposals",
  AUTOLOGIN: "autologin",
  SIGNED_IN: "signedIn",
  JWT_TOKEN: "jwt_token",
  SAVED_ACCOUNTS: "saved_accounts"
};

export interface SavedAccount {
  username: string;
  authType: 'wif' | 'keychain';
  autologin?: string | null;
  token?: string | null;
  savedAt: number;
}

interface userAuthValue {
    user: any;
    signedIn: boolean;
    autologin: any;
    savedAccounts: SavedAccount[];
    following: any[];
    metadata: any;
    rpc_node: string;
    app_settings: any;
    post_draft: any;
    recently_viewed: any[];
    notifications_viewed: any[];
    proposals: any[];
    login: (userData: any, autologinData?: any, rememberAccount?: boolean, authType?: 'wif' | 'keychain') => Promise<void>;
    logout: () => void; 
    switchAccount: (username: string) => Promise<boolean>;
    saveAccount: (accountData: SavedAccount) => void;
    removeSavedAccount: (username: string) => void;
    updateMetadata: Function;
    updateUserMetadata: Function;
    updateFollowing: Function;
    updateRPCNode: Function;
    updateAppSettings: Function;
    updateSiteSettings: Function;
    updatePostDraft: Function;
    updateRecentlyViewed: Function;
    updateNotificationsViewed: Function;
    updateProposals: Function;
    site_settings: any;
}

export const AuthContext = createContext<userAuthValue | undefined>(undefined);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(localStorage.getItem(storage.USER));
  const [autologin, setAutologin] = useState(localStorage.getItem(storage.AUTOLOGIN)); 
  const [savedAccounts, setSavedAccounts] = useState<SavedAccount[]>(() => {
    const stored = localStorage.getItem(storage.SAVED_ACCOUNTS);
    try {
      if (!stored || stored === "undefined") return [];
      return JSON.parse(stored);
    } catch {
      return [];
    }
  });

  const [recently_viewed, setRecentlyViewed] = useState(() => {
    const stored = localStorage.getItem(storage.RECENTLY_VIEWED);
    try { 
      if (!stored || stored === "undefined") return [];
      return JSON.parse(stored); 
    } catch { return []; }
  }); 
  const [notifications_viewed, setNotificationsViewed] = useState(() => {
    const stored = localStorage.getItem(storage.NOTIFICATIONS_VIEWED);
    try { 
      if (!stored || stored === "undefined") return [];
      return JSON.parse(stored); 
    } catch { return []; }
  }); 
  // const [following, setFollowing] = useState(localStorage.getItem(storage.FOLLOWING) || []);
  const [following, setFollowing] = useState(() => {
    const stored = localStorage.getItem(storage.FOLLOWING);
    try { 
      if (!stored || stored === "undefined") return [];
      return JSON.parse(stored); 
    } catch { return []; }
  });
  const [metadata, setMetadata] = useState(() => {
    const stored = localStorage.getItem(storage.METADATA);
    try { 
      if (!stored || stored === "undefined") return {};
      return JSON.parse(stored); 
    } catch { return {}; }
  });
  const [rpc_node, setRPCNode] = useState(localStorage.getItem(storage.RPC_NODE) || "https://api.steemit.com");
  const [app_settings, setAppSettings] = useState(() => {
    const stored = localStorage.getItem(storage.APP_SETTINGS);
    try { 
      if (!stored || stored === "undefined") return {};
      return JSON.parse(stored); 
    } catch { return {}; }
  });
  const [post_draft, setPostDraft] = useState(() => {
    const stored = localStorage.getItem(storage.POST_DRAFT);
    try { 
      if (!stored || stored === "undefined") return {};
      return JSON.parse(stored); 
    } catch { return {}; }
  });
  const [proposals, setProposals] = useState(() => {
    const stored = localStorage.getItem(storage.PROPOSALS);
    try {
      if (!stored || stored === "undefined") return [];
      return JSON.parse(stored);
    } catch {
      return [];
    }
  }); 
  const [signedIn, setSignedIn] = useState(() => {
    const stored = localStorage.getItem(storage.SIGNED_IN);
    try {
      if (stored === "undefined" || !stored) return false;
      return JSON.parse(stored);
    } catch {
      return false;
    }
  });

  const [site_settings, setSiteSettings] = useState<any>({ ads_enabled: 'false' });

  useEffect(() => {
    setUser(localStorage.getItem(storage.USER));
    setAutologin(localStorage.getItem(storage.AUTOLOGIN));
    
    const storedFollowing = localStorage.getItem(storage.FOLLOWING);
    try { setFollowing(storedFollowing && storedFollowing !== "undefined" ? JSON.parse(storedFollowing) : []); } catch { setFollowing([]); }

    const storedMetadata = localStorage.getItem(storage.METADATA);
    try { setMetadata(storedMetadata && storedMetadata !== "undefined" ? JSON.parse(storedMetadata) : {}); } catch { setMetadata({}); }

    setRPCNode(localStorage.getItem(storage.RPC_NODE) || "https://api.steemit.com"); 
    
    const storedSettings = localStorage.getItem(storage.APP_SETTINGS);
    try { setAppSettings(storedSettings && storedSettings !== "undefined" ? JSON.parse(storedSettings) : {}); } catch { setAppSettings({}); }

    const storedPost = localStorage.getItem(storage.POST_DRAFT);
    try { setPostDraft(storedPost && storedPost !== "undefined" ? JSON.parse(storedPost) : {}); } catch { setPostDraft({}); }

    const storedViewed = localStorage.getItem(storage.RECENTLY_VIEWED);
    try { setRecentlyViewed(storedViewed && storedViewed !== "undefined" ? JSON.parse(storedViewed) : []); } catch { setRecentlyViewed([]); }

    const storedNotificationsViewed = localStorage.getItem(storage.NOTIFICATIONS_VIEWED);
    try { setNotificationsViewed(storedNotificationsViewed && storedNotificationsViewed !== "undefined" ? JSON.parse(storedNotificationsViewed) : []); } catch { setNotificationsViewed([]); }

    const storedProposals = localStorage.getItem(storage.PROPOSALS);
    try {
      if (!storedProposals || storedProposals === "undefined") {
        setProposals([]);
      } else {
        setProposals(JSON.parse(storedProposals));
      }
    } catch {
      setProposals([]);
    }

    const storedSignedIn = localStorage.getItem(storage.SIGNED_IN);
    const storedToken = localStorage.getItem(storage.JWT_TOKEN);
    const storedUser = localStorage.getItem(storage.USER);
    const storedAutologin = localStorage.getItem(storage.AUTOLOGIN);

    try {
      if (storedSignedIn === "undefined" || !storedSignedIn) {
        setSignedIn(false);
      } else {
        const isSigned = JSON.parse(storedSignedIn);
        setSignedIn(isSigned);
        if (isSigned && storedUser) {
          checkAndRefreshToken(storedUser, storedToken, storedAutologin);
        }
      }
    } catch {
      setSignedIn(false);
    }

    const fetchSiteSettings = async () => {
      const settings = await get_site_settings();
      if (settings && Object.keys(settings).length > 0) {
        setSiteSettings(settings);
      }
    };
    fetchSiteSettings();
  }, []); 

  const checkAndRefreshToken = async (currentUser: string | null, currentToken: string | null, autologinData: string | null) => {
    if (!currentUser) return;

    let needsRefresh = false;
    if (!currentToken || currentToken === "undefined") {
      needsRefresh = true;
    } else {
      try {
        const parts = currentToken.split('.');
        if (parts.length === 3) {
          const payload = JSON.parse(atob(parts[1]));
          const nowSec = Math.floor(Date.now() / 1000);
          if (payload.username?.toLowerCase() !== currentUser.toLowerCase() || !payload.exp || (payload.exp - nowSec < 24 * 3600)) {
            needsRefresh = true;
          }
        } else {
          needsRefresh = true;
        }
      } catch {
        needsRefresh = true;
      }
    }

    if (!needsRefresh) return;

    // 1. Try refresh endpoint with existing token
    if (currentToken && currentToken !== "undefined") {
      try {
        const res = await backend_refresh_token(currentToken);
        if (res?.success && res.token && res.username?.toLowerCase() === currentUser.toLowerCase()
              && (localStorage.getItem(storage.USER) || "").toLowerCase() === currentUser.toLowerCase()) {
          localStorage.setItem(storage.JWT_TOKEN, res.token);
          setSavedAccounts(prev => prev.map(a => a.username.toLowerCase() === currentUser.toLowerCase() ? { ...a, token: res.token } : a));
          return;
        }
      } catch (e) {
        console.warn("[auth] Token refresh endpoint failed.");
      }
    }

    // 2. Try re-signing with autologin (WIF) if available
    if (autologinData) {
      try {
        const session = extractSessionData(autologinData);
        if (session && session[0] && session[1]) {
          const wif = session[1];
          if (session[0].toLowerCase() !== currentUser.toLowerCase()) {
            throw new Error("Stored login belongs to another account.");
          }
          const challenge = await backend_get_challenge(session[0]);
          const privateKey = dsteem.PrivateKey.fromString(wif);
          const signature = privateKey.sign(dsteem.cryptoUtils.sha256(challenge)).toString();
          const auth = await backend_login(session[0], signature, challenge);
          if (auth?.success && auth.token && auth.username?.toLowerCase() === currentUser.toLowerCase()
              && (localStorage.getItem(storage.USER) || "").toLowerCase() === currentUser.toLowerCase()) {
            localStorage.setItem(storage.JWT_TOKEN, auth.token);
            setSavedAccounts(prev => prev.map(a => a.username.toLowerCase() === currentUser.toLowerCase() ? { ...a, token: auth.token } : a));
            console.log("[auth] JWT auto-refreshed successfully using stored credentials");
            return;
          }
        }
      } catch (e) {
        console.warn("[auth] Auto-login key re-auth failed.");
      }
    }
  };

  const saveAccount = (accData: SavedAccount) => {
    setSavedAccounts(prev => {
      const filtered = prev.filter(a => a.username.toLowerCase() !== accData.username.toLowerCase());
      const updated = [accData, ...filtered];
      localStorage.setItem(storage.SAVED_ACCOUNTS, JSON.stringify(updated));
      return updated;
    });
  };

  const removeSavedAccount = (targetUser: string) => {
    setSavedAccounts(prev => {
      const updated = prev.filter(a => a.username.toLowerCase() !== targetUser.toLowerCase());
      localStorage.setItem(storage.SAVED_ACCOUNTS, JSON.stringify(updated));
      return updated;
    });
  };

  const switchAccount = async (targetUsername: string): Promise<boolean> => {
    const targetLower = targetUsername.toLowerCase().trim();
    const acc = savedAccounts.find(a => a.username.toLowerCase() === targetLower);
    if (!acc) return false;

    const username = acc.username;
    setUser(username);
    localStorage.setItem(storage.USER, username);

    if (acc.autologin) {
      setAutologin(acc.autologin);
      localStorage.setItem(storage.AUTOLOGIN, acc.autologin);
    } else {
      setAutologin(null);
      localStorage.removeItem(storage.AUTOLOGIN);
    }

    if (acc.token) {
      localStorage.setItem(storage.JWT_TOKEN, acc.token);
      checkAndRefreshToken(username, acc.token, acc.autologin || null);
    } else {
      localStorage.removeItem(storage.JWT_TOKEN);
      checkAndRefreshToken(username, null, acc.autologin || null);
    }

    setSignedIn(true);
    localStorage.setItem(storage.SIGNED_IN, JSON.stringify(true));

    try {
      const [proposalsList, followingList, userInfo] = await Promise.all([
        getUserProposalVotes(username),
        getUserFollowingList(username),
        getUser(username),
      ]);

      updateProposals(proposalsList?.voterProposals || []);
      updateFollowing(followingList || []);

      try {
        if (userInfo?.json_metadata && userInfo?.json_metadata.trim() !== "" && userInfo?.json_metadata.trim() !== "undefined") {
          updateMetadata(JSON.parse(userInfo?.json_metadata) || {});
        } else {
          updateMetadata({});
        }
      } catch (e) {
        updateMetadata({});
      }
    } catch (err) {
      console.error("Error switching user data:", err);
    }

    return true;
  };

  const login = async (userData: any, autologinData: any = null, rememberAccount = true, authType: 'wif' | 'keychain' = 'wif') => {
    if (autologinData) {
      setAutologin(autologinData);
      localStorage.setItem(storage.AUTOLOGIN, autologinData);
    }
  
    if (userData.token) {
        localStorage.setItem(storage.JWT_TOKEN, userData.token);
    }

    const username = typeof userData === 'string' ? userData : userData.username;
    setUser(username);
    localStorage.setItem(storage.USER, username);
  
    setSignedIn(true);
    localStorage.setItem(storage.SIGNED_IN, JSON.stringify(true));

    if (rememberAccount) {
      saveAccount({
        username,
        authType,
        autologin: autologinData || null,
        token: userData.token || null,
        savedAt: Date.now()
      });
    }
  
    try {
      const [proposalsList, followingList, userInfo] = await Promise.all([
        getUserProposalVotes(username),
        getUserFollowingList(username),
        getUser(username),
      ]);
      console.log(userInfo);
      
      updateProposals(proposalsList.voterProposals);
      updateFollowing(followingList);
      
      try {
        if (userInfo?.json_metadata && userInfo?.json_metadata.trim() !== "" && userInfo?.json_metadata.trim() !== "undefined") {
          updateMetadata(JSON.parse(userInfo?.json_metadata) || {});
        } else {
          updateMetadata({});
        }
      } catch (e) {
        updateMetadata({});
      }

      setFollowing(followingList);
      localStorage.setItem(storage.FOLLOWING, JSON.stringify(followingList));
    } catch (err) {
      console.error("Error getting user data:", err);
    }
  };

  const logout = () => {
    setUser(null);
    setAutologin(null);
    setFollowing([]);
    setProposals([]);
    setNotificationsViewed([]);
    setPostDraft({});
    setMetadata({});
    setSignedIn(false);

    localStorage.removeItem(storage.USER);
    localStorage.removeItem(storage.AUTOLOGIN);
    localStorage.removeItem(storage.FOLLOWING);
    localStorage.removeItem(storage.PROPOSALS);
    localStorage.removeItem(storage.POST_DRAFT);
    localStorage.removeItem(storage.METADATA);
    localStorage.removeItem(storage.NOTIFICATIONS_VIEWED);
    localStorage.removeItem(storage.JWT_TOKEN);
    localStorage.setItem(storage.SIGNED_IN, JSON.stringify(false));
    // Note: storage.SAVED_ACCOUNTS is intentionally preserved so user can quickly switch back to saved accounts
  };

  const updateFollowing = (list) => {
    setFollowing(list);
    localStorage.setItem(storage.FOLLOWING, JSON.stringify(list));
  };
  const updateRPCNode = (url) => {
    setRPCNode(url);
    localStorage.setItem(storage.RPC_NODE, (url));
  };
  const updateProposals = (list) => {
    setProposals(list);
    localStorage.setItem(storage.PROPOSALS, JSON.stringify(list));
  };
  const updateRecentlyViewed = (list) => {
    setRecentlyViewed(list);
    localStorage.setItem(storage.RECENTLY_VIEWED, JSON.stringify(list));
  };
  const updateNotificationsViewed = (list) => {
    setNotificationsViewed(list);
    localStorage.setItem(storage.NOTIFICATIONS_VIEWED, JSON.stringify(list));
  }; 
  const updateAppSettings = (newSettings) => {
    setAppSettings((prev) => {
      const merged = { ...prev, ...newSettings };
      localStorage.setItem(storage.APP_SETTINGS, JSON.stringify(merged));
      return merged;
    });
  };
  const updateSiteSettings = (newSettings) => {
    setSiteSettings((prev) => ({ ...prev, ...newSettings }));
  };
  const updatePostDraft = (list) => {
    setPostDraft(list);
    localStorage.setItem(storage.POST_DRAFT, JSON.stringify(list));
  };
  const updateMetadata = (list) => {//update the local storage
    setMetadata(list);
    localStorage.setItem(storage.METADATA, JSON.stringify(list));
  };
  async function updateUserMetadata () {//get the user from blockchain and refresh the data
      const [userInfo] = await Promise.all([ 
          getUser(user),
      ]);
      
      try {
        if (userInfo?.json_metadata && userInfo?.json_metadata.trim() !== "" && userInfo?.json_metadata.trim() !== "undefined") {
          updateMetadata(JSON.parse(userInfo?.json_metadata) || {});
        } else {
          updateMetadata({});
        }
      } catch (e) {
        updateMetadata({});
      }
  }

  return (
    <AuthContext.Provider value={{ 
      user, 
      signedIn,
      autologin,
      savedAccounts,
      following,
      metadata,
      rpc_node,
      app_settings,
      post_draft,
      recently_viewed,
      notifications_viewed, 
      proposals, 
      login, 
      logout,
      switchAccount,
      saveAccount,
      removeSavedAccount,
      updateMetadata,
      updateUserMetadata,
      updateFollowing,
      updateRPCNode,
      updateAppSettings,
      updateSiteSettings,
      updatePostDraft,
      updateRecentlyViewed, 
      updateNotificationsViewed,
      updateProposals,
      site_settings }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => React.useContext(AuthContext);